// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
/**
 * Composant: AuditLogsManagement
 * Interface pour consulter et filtrer les logs d'audit
 */

import React, { useState, useEffect } from 'react';
import {
  Shield,
  RefreshCw,
  Filter,
  X,
  Search,
  Eye,
  Calendar,
  User,
  Activity,
  TrendingUp,
  AlertCircle,
  ChevronUp,
  FileText,
  LogIn,
  LogOut,
  Download,
  Share2,
  Edit,
  Trash2,
  Plus,
} from 'lucide-react';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { AuditLog, AuditAction } from '@/utils/auditLogsAPI';
import { Button, Spinner, Badge } from '@/components/ui-v2';

import { useTranslation } from 'react-i18next';
/**
 * Mapper les actions d'audit vers des labels français
 */
// ACTION_LABELS will be populated using t() inside the component
let ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: '',
  UPDATE: '',
  DELETE: '',
  LOGIN: '',
  LOGOUT: '',
  EXPORT: '',
  SHARE: '',
};

/**
 * Icônes pour chaque action
 */
const ACTION_ICONS: Record<AuditAction, React.ReactNode> = {
  CREATE: <Plus className="w-3 h-3" />,
  UPDATE: <Edit className="w-3 h-3" />,
  DELETE: <Trash2 className="w-3 h-3" />,
  LOGIN: <LogIn className="w-3 h-3" />,
  LOGOUT: <LogOut className="w-3 h-3" />,
  EXPORT: <Download className="w-3 h-3" />,
  SHARE: <Share2 className="w-3 h-3" />,
};

/**
 * Composant principal de gestion des logs d'audit
 */
export function AuditLogsManagement() {
  const { t } = useTranslation();

  // Override ACTION_LABELS with translated values
  ACTION_LABELS = {
    CREATE: t('admin.auditLogs.creation'),
    UPDATE: t('admin.auditLogs.modification'),
    DELETE: t('admin.auditLogs.deletion'),
    LOGIN: t('admin.auditLogs.connection'),
    LOGOUT: t('admin.auditLogs.disconnection'),
    EXPORT: t('admin.auditLogs.export'),
    SHARE: t('admin.auditLogs.sharing'),
  };

  const {
    logs,
    pagination,
    statistics,
    loading,
    error,
    loadLogs,
    loadStatistics,
    clearError,
  } = useAuditLogs({ autoLoad: true });

  // État des filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    action: '' as AuditAction | '',
    userId: '',
    resourceType: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50,
  });

  // Modal détail log
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none cursor-pointer text-sm`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  /**
   * Charger les logs avec les filtres actuels
   */
  const handleLoadLogs = () => {
    const params: any = { page: filters.page, limit: filters.limit };
    if (filters.action) params.action = filters.action;
    if (filters.userId) params.userId = filters.userId;
    if (filters.resourceType) params.resourceType = filters.resourceType;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    loadLogs(params);
  };

  /**
   * Appliquer les filtres
   */
  const handleApplyFilters = () => {
    setFilters((prev) => ({ ...prev, page: 1 }));
    handleLoadLogs();
  };

  /**
   * Réinitialiser les filtres
   */
  const handleResetFilters = () => {
    setFilters({
      action: '',
      userId: '',
      resourceType: '',
      startDate: '',
      endDate: '',
      page: 1,
      limit: 50,
    });
    loadLogs({ page: 1, limit: 50 });
    loadStatistics();
  };

  /**
   * Rafraîchir les données
   */
  const handleRefresh = () => {
    handleLoadLogs();
    loadStatistics(filters.startDate, filters.endDate);
  };

  /**
   * Afficher les détails d'un log
   */
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  /**
   * Changer de page
   */
  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  /**
   * Recharger quand la page change
   */
  useEffect(() => {
    handleLoadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page]);

  /**
   * Formater une date
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  /**
   * Badge pour les actions
   */
  const getActionBadge = (action: AuditAction) => {
    const variants: Record<AuditAction, 'success' | 'info' | 'alert' | 'warning' | 'pending'> = {
      CREATE: 'success',
      UPDATE: 'info',
      DELETE: 'alert',
      LOGIN: 'success',
      LOGOUT: 'pending',
      EXPORT: 'warning',
      SHARE: 'info',
    };
    return (
      <Badge variant={variants[action]}>
        <span className="flex items-center gap-1.5">
          {ACTION_ICONS[action]}
          {ACTION_LABELS[action]}
        </span>
      </Badge>
    );
  };

  if (loading && !logs.length) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.auditLogs.title')}</h1>
            <p className="text-gray-500 mt-0.5">
              {t('admin.auditLogs.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={loading}
            icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
          >
            {t('admin.auditLogs.refresh')}
          </Button>
          <Button
            variant={showFilters ? 'secondary' : 'ghost'}
            onClick={() => setShowFilters(!showFilters)}
            icon={showFilters ? <ChevronUp className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
          >
            {t('admin.auditLogs.filters')}
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Activity className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('admin.auditLogs.isoTitle')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('admin.auditLogs.isoDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50/50 backdrop-blur-sm border border-red-200/50 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-red-100 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-red-900">{t('common.labels.error')}</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="p-2 text-red-600 hover:bg-red-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Panneau de filtres */}
      {showFilters && (
        <div className={cardClasses}>
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('admin.auditLogs.advancedFilters')}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Action */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.auditLogs.actionLabel')}
              </label>
              <select
                value={filters.action}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, action: e.target.value as AuditAction | '' }))
                }
                className={selectClasses}
              >
                <option value="">{t('admin.auditLogs.allActions')}</option>
                <option value="CREATE">{t('admin.auditLogs.creation')}</option>
                <option value="UPDATE">{t('admin.auditLogs.modification')}</option>
                <option value="DELETE">{t('admin.auditLogs.deletion')}</option>
                <option value="LOGIN">{t('admin.auditLogs.connection')}</option>
                <option value="LOGOUT">{t('admin.auditLogs.disconnection')}</option>
                <option value="EXPORT">{t('admin.auditLogs.export')}</option>
                <option value="SHARE">{t('admin.auditLogs.sharing')}</option>
              </select>
            </div>

            {/* Type de ressource */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.auditLogs.resourceType')}
              </label>
              <input
                type="text"
                placeholder={t('admin.auditLogs.resourceTypePlaceholder')}
                value={filters.resourceType}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, resourceType: e.target.value }))
                }
                className={inputClasses}
              />
            </div>

            {/* ID Utilisateur */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.auditLogs.userId')}
              </label>
              <input
                type="text"
                placeholder={t('admin.auditLogs.userIdPlaceholder')}
                value={filters.userId}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, userId: e.target.value }))
                }
                className={inputClasses}
              />
            </div>

            {/* Date début */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.auditLogs.startDate')}
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
                className={inputClasses}
              />
            </div>

            {/* Date fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.auditLogs.endDate')}
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
                className={inputClasses}
              />
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/30">
            <Button
              variant="secondary"
              onClick={handleApplyFilters}
              disabled={loading}
              icon={<Search className="w-4 h-4" />}
            >
              {t('admin.auditLogs.apply')}
            </Button>
            <Button variant="ghost" onClick={handleResetFilters}>
              {t('admin.auditLogs.reset')}
            </Button>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total */}
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.totalLogs')}</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.totalLogs}</p>
              </div>
              <div className="p-2 bg-teal-100 rounded-xl">
                <Activity className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </div>

          {/* Par action (top 3) */}
          {statistics.byAction?.slice(0, 3).map((stat, index) => {
            const icons = [User, TrendingUp, FileText];
            const Icon = icons[index];
            const colors = [
              { bg: 'bg-green-100', icon: 'text-green-600' },
              { bg: 'bg-blue-100', icon: 'text-blue-600' },
              { bg: 'bg-yellow-100', icon: 'text-yellow-600' },
            ];
            return (
              <div key={stat.action} className={cardClasses}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">
                      {ACTION_LABELS[stat.action]}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                  </div>
                  <div className={`p-2 ${colors[index].bg} rounded-xl`}>
                    <Icon className={`w-5 h-5 ${colors[index].icon}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table des logs */}
      <div className={`${cardClasses} overflow-hidden p-0`}>
        <div className="px-6 py-4 border-b border-white/30">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            {t('admin.auditLogs.actionsHistory')}
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/30 border-b border-white/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.auditLogs.dateTime')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.auditLogs.user')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.auditLogs.actionLabel')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.auditLogs.resource')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.auditLogs.ip')}
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {t('admin.auditLogs.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/20">
              {!logs || logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                      <Shield className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('admin.auditLogs.noLogs')}
                    </h3>
                    <p className="text-gray-600">
                      {t('admin.auditLogs.noLogsDescription')}
                    </p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-white/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(log.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">
                          {log.User?.fullName || t('admin.auditLogs.system')}
                        </p>
                        <p className="text-gray-500 text-xs">{log.User?.email || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getActionBadge(log.action)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{log.entityType}</p>
                        <p className="text-gray-500 text-xs truncate max-w-[200px]">
                          {log.entityId}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {log.ipAddress || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleViewDetails(log)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-700 bg-teal-50
                          rounded-xl hover:bg-teal-100 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {t('admin.auditLogs.details')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-white/30 border-t border-white/30 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {t('admin.auditLogs.page', { current: pagination.currentPage, total: pagination.totalPages, count: pagination.total })}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1 || loading}
              >
                {t('admin.auditLogs.previous')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages || loading}
              >
                {t('admin.auditLogs.next')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal détails du log */}
      {showDetailModal && selectedLog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-white/50">
            {/* Header modal */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('admin.auditLogs.logDetails')}</h3>
                    <p className="text-teal-100 text-sm">
                      {formatDate(selectedLog.createdAt)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Informations générales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.actionLabel')}</p>
                  {getActionBadge(selectedLog.action)}
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.dateTimeLabel')}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(selectedLog.createdAt)}
                  </p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.userLabel')}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedLog.User?.fullName || t('admin.auditLogs.system')}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedLog.User?.email || '-'}
                  </p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.resourceTypeLabel')}</p>
                  <p className="text-sm font-medium text-gray-900">{selectedLog.entityType}</p>
                </div>
                <div className="col-span-2 bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.resourceId')}</p>
                  <p className="text-sm font-mono text-gray-900 break-all">
                    {selectedLog.entityId}
                  </p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.ipAddress')}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedLog.ipAddress || '-'}
                  </p>
                </div>
                <div className="bg-gray-50/50 rounded-2xl p-4">
                  <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.auditLogs.userAgent')}</p>
                  <p
                    className="text-sm text-gray-900 truncate"
                    title={selectedLog.userAgent || '-'}
                  >
                    {selectedLog.userAgent || '-'}
                  </p>
                </div>
              </div>

              {/* {t('admin.auditLogs.oldValues')} */}
              {selectedLog.oldValues && Object.keys(selectedLog.oldValues).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    {t('admin.auditLogs.oldValues')}
                  </p>
                  <div className="bg-red-50/50 rounded-2xl p-4 border border-red-100">
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-x-auto font-mono">
                      {JSON.stringify(selectedLog.oldValues, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* {t('admin.auditLogs.newValues')} */}
              {selectedLog.newValues && Object.keys(selectedLog.newValues).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {t('admin.auditLogs.newValues')}
                  </p>
                  <div className="bg-green-50/50 rounded-2xl p-4 border border-green-100">
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-x-auto font-mono">
                      {JSON.stringify(selectedLog.newValues, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Footer modal */}
            <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-200">
              <Button variant="ghost" onClick={() => setShowDetailModal(false)} className="w-full">
                {t('admin.auditLogs.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
